
import React from 'react';
import { createRoot } from 'react-dom/client';
import domtoimage from 'dom-to-image';
import structuredDataRegistry from './structuredDataRegistry';
import { BullseyeDiagram } from './Bullseye/BullseyeDiagram';
import { SimpleBullseyeDiagram, CONCLUSION_ANOMALY_COLORS } from './Bullseye/StressBullseye';
import { captureStressBullseyeFromDOM, captureStandardBullseyeFromDOM } from './renderStressBullseyeForExport';
import { createLogger } from '../services/Debug';

/**
 * Renders a stress test Bullseye (5 diagrams: 4 stages + conclusion)
 * @param {Object} structuredData - The stress test data
 * @param {Object} options - Rendering options
 * @returns {Promise<string>} Base64 data URL of the rendered image
 */
async function renderStressBullseyeImage(structuredData, options = {}) {
  const log = createLogger('renderStressBullseyeImage');
  // First try to capture from existing DOM (this gives the exact same result as download)
  const domCapture = await captureStressBullseyeFromDOM(options);
  if (domCapture) {
    
    return domCapture;
  }
  
  // Fallback to creating a new element if DOM capture fails
  
  const width = options.width || 1100; // Increased width to capture all content
  const scale = options.scale || 4;
  const outputDownscaleFactor = typeof options.outputDownscaleFactor === 'number'
    ? options.outputDownscaleFactor
    : 0.5; // Default 0.5 to maintain original size with higher quality

  // Create an off-screen container
  const container = document.createElement('div');
  container.style.position = 'fixed';
  container.style.left = '-10000px';
  container.style.top = '-10000px';
  container.style.width = `${width}px`;
  container.style.minWidth = `${width}px`;
  container.style.zIndex = '-1';
  container.style.backgroundColor = '#ffffff';
  document.body.appendChild(container);

  let root;
  try {
    root = createRoot(container);
    
    // Create the stress test layout (similar to StressBullseyeView but simplified)
    const stressStages = [
      { id: 'repos', label: 'Repos' },
      { id: 'low_dose', label: 'Effort léger' },
      { id: 'pic_stress', label: 'Pic d\'effort' },
      { id: 'recuperation', label: 'Récupération' }
    ];

    // Ensure all stages have data
    const normalizedData = {
      repos: structuredData.repos || {},
      low_dose: structuredData.low_dose || structuredData.stress_leger || {},
      pic_stress: structuredData.pic_stress || {},
      recuperation: structuredData.recuperation || {},
      conclusion: structuredData.conclusion || {}
    };

    const stressLayout = React.createElement('div', {
      id: 'stress-bullseye-export',
      style: {
        backgroundColor: '#ffffff',
        padding: '20px',
        fontFamily: 'Arial, sans-serif',
        width: '100%',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px'
      }
    },
      // Main container - match the actual layout from StressBullseye (NO TITLE!)
      React.createElement('div', {
        style: {
          display: 'flex',
          gap: '20px',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%'
        }
      },
        // 4 Stage Bullseyes in 2x2 grid
        React.createElement('div', {
          style: {
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '16px'
          }
        },
          ...stressStages.map(stage =>
            React.createElement(SimpleBullseyeDiagram, {
              key: stage.id,
              title: stage.label,
              value: normalizedData[stage.id],
              width: 220,
              height: 220,
              showLabels: true,
              onChange: () => {} // No-op for static rendering
            })
          )
        ),
        
        // Divider
        React.createElement('div', {
          style: {
            width: '2px',
            minHeight: '460px',
            backgroundColor: '#e5e7eb',
            marginLeft: '20px',
            marginRight: '20px'
          }
        }),
        
        // Conclusion Bullseye
        React.createElement('div', {
          style: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center'
          }
        },
          React.createElement(SimpleBullseyeDiagram, {
            title: 'Conclusion',
            value: normalizedData.conclusion,
            width: 280,
            height: 280,
            showLabels: true,
            isConclusion: true,
            anomalyColors: CONCLUSION_ANOMALY_COLORS,
            onChange: () => {} // No-op for static rendering
          })
        )
      )
    );

    root.render(stressLayout);

    // Wait for render to complete (increased time for complex layout)
    await new Promise(resolve => setTimeout(resolve, 200));

    const node = container.querySelector('#stress-bullseye-export');
    if (!node) {
      throw new Error('Stress Bullseye node not found for rendering');
    }

    // Force layout calculation
    void node.offsetHeight; // Trigger reflow
    
    // Get actual dimensions using getBoundingClientRect for accuracy
    const rect = node.getBoundingClientRect();
    const exportWidth = Math.max(rect.width, node.scrollWidth || 0, node.offsetWidth || width);
    const exportHeight = Math.max(rect.height, node.scrollHeight || 0, node.offsetHeight || 600);

    const dataUrl = await domtoimage.toPng(node, {
      width: exportWidth * scale,
      height: exportHeight * scale,
      style: {
        transform: `scale(${scale})`,
        transformOrigin: 'top left',
        backgroundColor: '#ffffff'
      },
      quality: 1,
      bgcolor: '#ffffff'
    });

    // Optionally downscale
    if (outputDownscaleFactor > 0 && outputDownscaleFactor < 1) {
      const img = await new Promise((resolve, reject) => {
        const i = new Image();
        i.onload = () => resolve(i);
        i.onerror = reject;
        i.src = dataUrl;
      });

      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.floor(img.width * outputDownscaleFactor));
      canvas.height = Math.max(1, Math.floor(img.height * outputDownscaleFactor));
      const ctx = canvas.getContext('2d');
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      return canvas.toDataURL('image/png');
    }

    return dataUrl;
  } finally {
    try {
      if (root) {
        root.unmount();
      }
    } catch (_) {}
    if (container && container.parentNode) {
      container.parentNode.removeChild(container);
    }
  }
}




async function renderIRMCardiaqueImage(structuredData, options = {}) {
  // First try to capture from existing DOM
  const domCapture = await captureStandardBullseyeFromDOM(options, "irm-bullseye-wrapper");
  if (domCapture) {
    return domCapture;
  }
  
  // Fallback to creating a new element
  const width = options.width || 900;
  const scale = options.scale || 4;
  const outputDownscaleFactor = typeof options.outputDownscaleFactor === 'number'
    ? options.outputDownscaleFactor
    : 0.5; // Default 0.5 to maintain original size with higher quality

  const container = document.createElement('div');
  container.style.position = 'fixed';
  container.style.left = '-10000px';
  container.style.top = '-10000px';
  container.style.width = `${width}px`;
  container.style.zIndex = '-1';
  container.style.backgroundColor = '#ffffff';
  document.body.appendChild(container);

  let root;
  try {
    root = createRoot(container);
    
    // Create the IRM layout (3 bullseyes)
    const irmLayout = React.createElement('div', {
      id: 'irm-bullseye-export',
      style: {
        backgroundColor: '#ffffff',
        padding: '20px',
        fontFamily: 'Arial, sans-serif',
        width: '100%',
        display: 'flex',
        gap: '20px',
        justifyContent: 'center'
      }
    },
      ...['cinetique', 'viabilite', 'perfusion'].map(type =>
        React.createElement(SimpleBullseyeDiagram, {
          key: type,
          title: type.charAt(0).toUpperCase() + type.slice(1),
          value: structuredData[type] || {},
          width: 260,
          height: 260,
          showLabels: true,
          anomalyColors: getIRMColors(type),
          onChange: () => {}
        })
      )
    );

    root.render(irmLayout);
    await new Promise(resolve => setTimeout(resolve, 200));

    const node = container.querySelector('#irm-bullseye-export');
    if (!node) {
      throw new Error('IRM Bullseye node not found');
    }

    const dataUrl = await domtoimage.toPng(node, {
      width: width * scale,
      height: 600 * scale,
      style: {
        transform: `scale(${scale})`,
        transformOrigin: 'top left',
      },
      quality: 1.0,
      bgcolor: '#ffffff'
    });

    // Optionally downscale
    if (outputDownscaleFactor > 0 && outputDownscaleFactor < 1) {
      // ... downscale logic ...
    }

    return dataUrl;
  } finally {
    if (root) root.unmount();
    if (container && container.parentNode) {
      container.parentNode.removeChild(container);
    }
  }
}

// Helper function to get colors for each IRM type
function getIRMColors(type) {
  switch(type) {
    case 'cinetique':
      return {
        "Normal": "#4ade80",
        "Hypokinésie légère": "#fbbf24",
        "Hypokinésie sévère": "#fb923c",
        "Akinésie": "#ef4444"
      };
    case 'viabilite':
      return {
        "Viable": "#4ade80",
        "Hibernation": "#60a5fa",
        "Ischémie": "#fbbf24",
        "Non viable": "#6b7280"
      };
    case 'perfusion':
      return {
        "Normal": "#4ade80",
        "Déficit léger": "#a78bfa",
        "Déficit modéré": "#8b5cf6",
        "Déficit sévère": "#7c3aed"
      };
    default:
      return {};
  }
}


/**
 * Renders a standard ETT Bullseye (single diagram)
 * @param {Object} structuredData - The ETT data
 * @param {Object} options - Rendering options
 * @returns {Promise<string>} Base64 data URL of the rendered image
 */
async function renderStandardBullseyeImage(structuredData, options = {}) {
  // First try to capture from existing DOM (this gives the exact same result as download)
  const domCapture = await captureStandardBullseyeFromDOM(options);
  if (domCapture) {
    
    return domCapture;
  }
  
  // Fallback to creating a new element if DOM capture fails

  const width = options.width || 280;
  const scale = options.scale || 4;
  const outputDownscaleFactor = typeof options.outputDownscaleFactor === 'number'
    ? options.outputDownscaleFactor
    : 0.5; // Default 0.5 to maintain original size with higher quality

  const container = document.createElement('div');
  container.style.position = 'fixed';
  container.style.left = '-10000px';
  container.style.top = '-10000px';
  container.style.width = `${width}px`;
  container.style.zIndex = '-1';
  document.body.appendChild(container);

  let root;
  try {
    root = createRoot(container);
    root.render(
      React.createElement('div', { 
        id: 'bullseye-wrapper', 
        style: { 
          width: `${width}px`,
          backgroundColor: '#ffffff',
          padding: '10px'
        } 
      },
        React.createElement(BullseyeDiagram, { 
          value: structuredData || {}, 
          width, 
          height: options.height || 280,
          onChange: () => {} // No-op for static rendering
        })
      )
    );

    // Wait for render flush
    await new Promise((resolve) => setTimeout(resolve, 50));

    const node = container.querySelector('#bullseye-wrapper');
    if (!node) {
      throw new Error('Bullseye node not found for rendering');
    }

    // Capture full rendered dimensions (including legend)
    const exportWidth = Math.max(node.scrollWidth || 0, node.offsetWidth || width);
    const exportHeight = Math.max(node.scrollHeight || 0, node.offsetHeight || (options.height || 280));

    const dataUrl = await domtoimage.toPng(node, {
      width: exportWidth * scale,
      height: exportHeight * scale,
      style: {
        transform: `scale(${scale})`,
        transformOrigin: 'top left',
        backgroundColor: '#ffffff'
      },
      quality: 1,
      bgcolor: '#ffffff',
    });

    // Optionally downscale the final PNG
    if (outputDownscaleFactor > 0 && outputDownscaleFactor < 1) {
      const img = await new Promise((resolve, reject) => {
        const i = new Image();
        i.onload = () => resolve(i);
        i.onerror = reject;
        i.src = dataUrl;
      });

      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.floor(img.width * outputDownscaleFactor));
      canvas.height = Math.max(1, Math.floor(img.height * outputDownscaleFactor));
      const ctx = canvas.getContext('2d');
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      return canvas.toDataURL('image/png');
    }

    return dataUrl;
  } finally {
    try {
      if (root) {
        root.unmount();
      }
    } catch (_) {}
    if (container && container.parentNode) {
      container.parentNode.removeChild(container);
    }
  }
}

/**
 * Main unified function to render any Bullseye image based on structured data type
 * @param {Object} structuredData - The structured data to render
 * @param {Object} options - Rendering options
 * @returns {Promise<string|null>} Base64 data URL of the rendered image or null if not supported
 */
export async function renderBullseyeImageUnified(structuredData, options = {}) {
  if (!structuredData) return null;
  const log = createLogger('renderBullseyeImageUnified');
  // Parse the data to handle any transformations
  const parsedData = structuredDataRegistry.parseData(structuredData);
  
  // Detect the type of structured data
  const dataType = structuredDataRegistry.detectType(parsedData);
  
  if (!dataType) {
    log.error('Unable to detect structured data type for rendering');
    return null;
  }

    

  try {
    switch (dataType.component) {
      case 'StressBullseye':
        return await renderStressBullseyeImage(parsedData, options);
      
      case 'IRMCardiaqueBullseye':
        return await renderIRMCardiaqueImage(parsedData, options);

      case 'StandardBullseye':
      default:
        return await renderStandardBullseyeImage(parsedData, options);
      

      // Future renderers can be added here:
      // case 'CoronaryDiagram':
      //   return await renderCoronaryDiagramImage(parsedData, options);
    }
  } catch (error) {
    log.error(`Error rendering ${dataType.name} image:`, error);
    return null;
  }
}

/**
 * Check if structured data can be rendered as an image
 * @param {Object} structuredData - The structured data to check
 * @returns {boolean} True if the data can be rendered
 */
export function canRenderBullseyeImage(structuredData) {
  return structuredDataRegistry.hasStructuredData(structuredData);
}

/**
 * Get the type name for the structured data
 * @param {Object} structuredData - The structured data
 * @returns {string|null} The type name or null
 */
export function getStructuredDataTypeName(structuredData) {
  const type = structuredDataRegistry.detectType(structuredData);
  return type ? type.name : null;
}

// Export for backward compatibility
export { renderStandardBullseyeImage as renderBullseyeImage };